import React, { useCallback, useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import VStack from "../../ui_atoms/VStack";
import Swipeable from "react-native-gesture-handler/ReanimatedSwipeable";
import Gap from "../../ui_atoms/Gap";
import { useTheme } from "../../hooks/use-theme";
import { DurationType, ScheduleTimeType, ScheduleType } from "../../__generated__/graphql";
import TimeField from "../TimeField";
import { useI18n } from "../../hooks/i18n-hooks";
import { DayOfWeek, MINIMUM_SLOT_DURATION_HOURS, scheduleTimeTypeTags } from "../../utils";
import { Buildable } from "ts-essentials";
import { useDropdown } from "../../hooks/use-dropdown";
import ListItem from "../../ui_atoms/ListItem";
import { DropdownItemsType } from "../../contexts/DropdownManager";
import IconButton from "../IconButton";
import Icon from "../Icon";
import { isArray, isString } from "lodash";
import Text from "../Text";
import { useIntercom } from "../../hooks/intercom";
import { checkScheduleTypeList, ScheduleTypeErrorInfo } from "./helpers";

const sortTimes = (times: ScheduleType[]): ScheduleType[] => {
    return times.sort((a, b) => a.startTime - b.startTime);
};

const getLastEndTime = (times: ScheduleType[]): number => {
    sortTimes(times);
    const lastSchedule = times[times.length - 1];
    return lastSchedule?.endTime || 0;
};

const ScheduleSlotInput: React.FC<{
    index: number;
    value: [number, number, ScheduleTimeType | null];
    onChange: (value: [number, number, ScheduleTimeType | null]) => void;
    onRemove?: () => void;
    disabled?: boolean;
    isSwipping?: boolean;
    error?: boolean | string | string[];
    day: DayOfWeek | string;
    disabledScheduleTimeTypes?: ScheduleTimeType[];
}> = ({
    value,
    onChange,
    disabled = false,
    isSwipping = false,
    error = false,
    onRemove,
    day,
    index,
    disabledScheduleTimeTypes = [],
}) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { presentArticle } = useIntercom();

    const componentStyle = useScheduleSlotStyle();
    const onChangeStart = useCallback(
        (time: number) => {
            onChange([time, value[1], value[2]]);
        },
        [onChange, value],
    );
    const dropDownItems = useMemo(() => {
        const base: DropdownItemsType[] = scheduleTimeTypeTags
            .filter((t) => !disabledScheduleTimeTypes.includes(t.value))
            .map((tag) => ({
                ...tag,
                label: i18n.t(`workingTime.type.${tag.value}`),
                selected: tag.value === value[2],
                onClick: () => {
                    onChange([value[0], value[1], tag.value]);
                },
                color: undefined,
            }));
        // add help
        base.push({
            label: i18n.t(`workingTime.dropdown_show_intercom`),
            selected: false,
            color: colors.secondary,
            icon: "lightbulb",
            style: { borderTopWidth: 1, borderColor: colors.accentLighter },
            multiline: true,
            onClick: () => {
                void presentArticle("WorkingTimeTypes");
            },
        });
        if (onRemove) {
            base.push({
                label: i18n.t(`workingTime.removeScheduleSlot`),
                selected: false,
                color: colors.error,
                icon: "trash",
                style: { borderTopWidth: 1, borderColor: colors.accentLighter },
                onClick: onRemove,
            });
        }
        return base;
    }, [
        colors.accentLighter,
        colors.error,
        colors.secondary,
        disabledScheduleTimeTypes,
        i18n,
        onChange,
        onRemove,
        presentArticle,
        value,
    ]);

    const dropdownConfig = useDropdown({
        items: dropDownItems,
        withScreenActionsPadding: false,
    });

    const onChangeEnd = useCallback(
        (time: number) => {
            if (time == 0) {
                time = 24 * 60;
            }
            onChange([value[0], time, value[2]]);
        },
        [onChange, value],
    );
    return (
        <View style={componentStyle.container}>
            <View
                style={[componentStyle.containerRow, error ? componentStyle.containerError : null]}
            >
                {error && <View style={componentStyle.errorBadge}></View>}

                <TimeField
                    // Si plusieurs fois Lundi -> incorrect
                    setTimeFn={`${day}_${index}_start`}
                    style={[componentStyle.field, isSwipping && componentStyle.swipping]}
                    inputStyle={componentStyle.inputField}
                    value={value[0]}
                    onChange={onChangeStart}
                    minuteInterval={1}
                    disabled={disabled}
                    allowFontScaling={false}
                    error={!!error}
                    showErrorText={false}
                />
                <Gap gap={0.5} />
                <TimeField
                    setTimeFn={`${day}_${index}_end`}
                    style={[componentStyle.field, isSwipping && componentStyle.swipping]}
                    inputStyle={componentStyle.inputField}
                    value={value[1]}
                    onChange={onChangeEnd}
                    minuteInterval={1}
                    disabled={disabled}
                    allowFontScaling={false}
                    error={!!error}
                    showErrorText={false}
                />
                <View style={componentStyle.spacer} />
                {!!value[2] && (
                    <IconButton
                        icon={
                            scheduleTimeTypeTags.find((tag) => tag.value === value[2])?.icon ||
                            "help"
                        }
                        mode="outlined"
                        style={componentStyle.actionButton}
                        size={48}
                        disabled={disabled}
                        {...dropdownConfig}
                    />
                )}
            </View>

            {error &&
                isArray(error) &&
                error.map((r, idx) => (
                    <Text key={`${idx}:${r}`} style={componentStyle.errorText}>
                        {i18n.t(r)}
                    </Text>
                ))}
            {error && isString(error) && (
                <Text style={componentStyle.errorText}>{i18n.t(error)}</Text>
            )}
        </View>
    );
};

const useScheduleSlotStyle = () => {
    const { gridSize, colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    flexDirection: "column",
                    alignItems: "flex-start",
                    display: "flex",
                    alignSelf: "stretch",
                    paddingVertical: gridSize / 2,
                    backgroundColor: colors.background,
                    borderBottomWidth: 1,
                    borderBottomColor: colors.accentLighter,
                    paddingHorizontal: gridSize / 2,
                },
                containerRow: {
                    flexDirection: "row",
                    alignItems: "center",
                    display: "flex",
                    alignSelf: "stretch",
                    justifyContent: "flex-start",
                },
                containerError: {
                    //backgroundColor: colors.errorBackground,
                },
                errorBadge: {
                    position: "absolute",
                    top: 2,
                    left: 2,
                    bottom: 2,
                    backgroundColor: colors.error,
                    width: 4,
                    borderRadius: 4,
                    overflow: "hidden",
                },
                startGap: {
                    width: gridSize / 2,
                },
                duration: {
                    width: 50,
                    textAlign: "center",
                },
                field: {
                    width: "auto",
                    flexGrow: 0,
                    backgroundColor: colors.background,
                    textAlign: "center",
                },
                inputField: {
                    ...fonts.labelLarge,
                },
                nonField: {
                    marginTop: 8,
                },
                swipping: {
                    backgroundColor: "transparent",
                    color: colors.background,
                },
                spacer: {
                    flex: 2,
                    flexGrow: 1,
                    flexShrink: 1,
                    minWidth: gridSize / 2,
                },
                actionButton: {
                    flexGrow: 0,
                    flexShrink: 0,
                    alignSelf: "center",
                    margin: 0,
                    opacity: 1, // IMPORTANT NEEDED FOR Android measureInWindow
                },
                errorText: {
                    ...fonts.bodySmall,
                    color: colors.error,
                    paddingTop: gridSize / 4,
                },
            }),
        [gridSize, colors, fonts],
    );
};

type TimesComponentProps = {
    times: ScheduleType[];
    onChange: (times: ScheduleType[]) => void;
    disabled?: boolean;
    day: DayOfWeek | string;
    disabledScheduleTimeTypes?: ScheduleTimeType[];
    vacationOverflow?: number;
};

export const verifyScheduleTypesFixedTimes = (
    times?: Array<Buildable<ScheduleType> | undefined> | null,
): string[] => {
    const errors = Object.keys(checkScheduleTypeList(times).errors);
    return errors;
};

export const verifyScheduleTypesFreeDuration = (
    freeDurations?: Buildable<Array<DurationType>>,
): string[] => {
    const errors = [];
    if (
        !freeDurations ||
        !freeDurations[0] ||
        (freeDurations[0].duration && freeDurations[0].duration <= MINIMUM_SLOT_DURATION_HOURS * 60)
    ) {
        errors.push("workingTime.tooLowDuration");
    }
    return errors;
};

const TimeListComponent: React.FC<TimesComponentProps> = ({
    times,
    onChange,
    disabled = false,
    day,
    disabledScheduleTimeTypes,
    vacationOverflow,
}) => {
    const i18n = useI18n();
    const componentStyle = useTimeListComponentStyle();
    const renderSwipeRightActions = React.useCallback(() => {
        return (
            <View style={componentStyle.swipeRightItem}>
                <Icon size={24} name="trash" color="white" />
            </View>
        );
    }, [componentStyle]);

    const [isSwipping, setIsSwipping] = useState<boolean[]>(new Array(times.length).fill(false));

    const _onChangeTimes = useCallback(
        (newTimes: ScheduleType[]) => {
            onChange(newTimes);
        },
        [onChange],
    );

    const onAddHours = useCallback(() => {
        const newTimes = [...times];
        const lastEndTime = getLastEndTime(newTimes);
        newTimes.push({
            startTime: lastEndTime,
            endTime: lastEndTime + 60,
            type: ScheduleTimeType.Work,
        });
        sortTimes(newTimes);
        _onChangeTimes(newTimes);
    }, [_onChangeTimes, times]);

    const onRemoveHours = useCallback(
        (index: number) => {
            const newTimes = [...times];
            newTimes.splice(index, 1);
            _onChangeTimes(newTimes);

            const newIsSwipping = [...isSwipping];
            newIsSwipping.splice(index, 1);
            setIsSwipping(newIsSwipping);
        },
        [_onChangeTimes, times, isSwipping],
    );

    const onChangeHours = useCallback(
        (index: number, value: [number, number, ScheduleTimeType | null]) => {
            const newTimes = [...times];
            newTimes[index] = {
                startTime: value[0],
                endTime: value[1],
                type: value[2] || ScheduleTimeType.Work,
            };
            sortTimes(newTimes);
            _onChangeTimes(newTimes);
        },
        [_onChangeTimes, times],
    );

    const errors = useMemo<ScheduleTypeErrorInfo>(() => {
        return checkScheduleTypeList(times, vacationOverflow);
    }, [times, vacationOverflow]);

    return (
        <View style={componentStyle.container}>
            <VStack alignment="stretch">
                {times.map((x, idx) => (
                    <Swipeable
                        enabled={!disabled}
                        key={`${idx}:${x.startTime}-${x.endTime}`}
                        renderRightActions={renderSwipeRightActions}
                        onSwipeableOpen={() => {
                            onRemoveHours(idx);
                        }}
                        onActivated={() => {
                            setIsSwipping((_isSwipping) =>
                                _isSwipping.map((x, i) => (i === idx ? true : x)),
                            );
                        }}
                        onEnded={() => {
                            setIsSwipping((_isSwipping) =>
                                _isSwipping.map((x, i) => (i === idx ? false : x)),
                            );
                        }}
                    >
                        <ScheduleSlotInput
                            key={`${idx}:${x.startTime}-${x.endTime}`}
                            index={idx}
                            day={day}
                            value={[x.startTime, x.endTime, x.type]}
                            onChange={(value) => {
                                onChangeHours(idx, value);
                            }}
                            onRemove={() => onRemoveHours(idx)}
                            disabled={disabled}
                            isSwipping={isSwipping[idx]}
                            error={
                                errors.indexErrors[idx].length > 0
                                    ? errors.indexErrors[idx]
                                    : undefined
                            }
                            disabledScheduleTimeTypes={disabledScheduleTimeTypes}
                        />
                    </Swipeable>
                ))}
            </VStack>
            {!disabled && (
                <View>
                    <ListItem
                        testID={`${day}-add-schedule-slot`}
                        labels={[{ text: i18n.t("workingTime.addScheduleSlot"), mode: "cta" }]}
                        onPress={onAddHours}
                        rightIcon="add-large"
                        rightActionMode="action_simple"
                    />
                </View>
            )}
        </View>
    );
};

const useTimeListComponentStyle = () => {
    const { colors, gridSize } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    display: "flex",
                    flexDirection: "column",
                    alignSelf: "stretch",
                },
                swipeRightItem: {
                    flex: 1,
                    backgroundColor: colors.notification,
                    alignItems: "flex-end",
                    justifyContent: "center",
                    paddingRight: gridSize,
                },
                addButton: {
                    flex: 1,
                    backgroundColor: "transparent",
                    elevation: 0,
                },
            }),
        [colors, gridSize],
    );
};

export default TimeListComponent;
